/**
 * lib/i18n.js
 * Lightweight internationalisation module.
 *
 * Strings live in /public/locales/{locale}.json — one flat file per language.
 * Adding a new language = drop a new JSON file. Zero code changes required.
 *
 * Supported locales: listed in SUPPORTED below.
 *
 * Detection order:
 *   1. localStorage saved preference
 *   2. Firestore saved preference (authenticated pages)
 *   3. Cached IP geolocation (ipapi.co) → GR/CY → 'el'
 *   4. navigator.language fallback
 *   5. Live IP geolocation fallback
 *   6. Default: 'en'
 *
 * Persistence:
 *   - Guests     → localStorage
 *   - Logged-in  → Firestore users/{uid}.locale  (synced lazily)
 *
 * DOM binding attributes:
 *   data-i18n="key"              → el.textContent
 *   data-i18n-html="key"         → el.innerHTML  (use for titles with <br>/<em>)
 *   data-i18n-placeholder="key"  → el.placeholder
 *   data-i18n-aria="key"         → el.aria-label
 *   data-i18n-title-attr="key"   → el.title
 *
 * Page title:  <html data-i18n-page-title="key">
 *
 * Translation key format: "namespace.key"  (flat dot-notation strings in JSON)
 * Interpolation:  t('key', { name: 'Kostas' })  →  {{name}} is replaced.
 */

// ─── Constants ────────────────────────────────────────────────────────────────
const STORAGE_KEY = "rwx_locale";
const IP_LOCALE_CACHE_KEY = "rwx_locale_ip_cache";
const IP_COUNTRY_CACHE_KEY = "rwx_country_cache";
const IP_LOCALE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const IP_LOCALE_ERROR_CACHE_TTL_MS = 60 * 60 * 1000;
export const SUPPORTED = ["en", "el"];

/**
 * Display labels for each supported locale.
 * Add a new entry here when adding a new language — no other code changes needed.
 * Flag emoji are Unicode and require no image assets.
 */
export const SUPPORTED_LABELS = {
  en: "🇬🇧",
  el: "🇬🇷",
};

const DEFAULT_LOCALE = "en"; // string fallback — EN is always preloaded for missing keys

// ─── Runtime state ────────────────────────────────────────────────────────────
let _locale = DEFAULT_LOCALE;
let _strings = {}; // active locale  { "key": "string" }
let _fallback = {}; // EN fallback     { "key": "string" }
let _ipLocalePromise = null;

// ─── Loader ───────────────────────────────────────────────────────────────────
async function _loadStrings(locale) {
  // Strings live in static/locales/ (copied to root /locales/ in build/dist)
  const res = await fetch(`/locales/${locale}.json`, { cache: "no-store" });
  if (!res.ok) throw new Error(`[i18n] Failed to load locale: ${locale}`);
  return res.json();
}

// ─── Core API ─────────────────────────────────────────────────────────────────

/**
 * Look up a translation by dot-notation key.
 * Falls back to English if a key is missing in the active locale.
 * Supports {{param}} interpolation.
 */
export function t(key, params = {}) {
  let str = _strings[key] ?? _fallback[key];
  if (str === undefined) return key;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => params[k] ?? `{{${k}}}`);
}

export function getLocale() {
  return _locale;
}

/**
 * Returns the IP-detected country from cache, or null if not yet detected.
 * Shape: { code: "GR", name: "Greece" } | null
 * Non-blocking — reads only from localStorage; never triggers a network call.
 */
export function getDetectedCountry() {
  try {
    const raw = localStorage.getItem(IP_COUNTRY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.code || !parsed?.expiresAt) return null;
    if (Date.now() > parsed.expiresAt) return null;
    return { code: parsed.code, name: parsed.name || "" };
  } catch {
    return null;
  }
}

/**
 * Apply translations to all [data-i18n*] elements in the document.
 * Also updates <html lang> and optionally the <title>.
 */
/**
 * Populate all .lang-select <select> elements with options from SUPPORTED and
 * sync their current value. Safe to call multiple times — options are only
 * inserted once (guarded by data-populated).
 */
function _populateLangSelects(activeLang) {
  document.querySelectorAll(".lang-select").forEach((sel) => {
    if (!sel.dataset.populated) {
      SUPPORTED.forEach((code) => {
        const opt = document.createElement("option");
        opt.value = code;
        opt.textContent = SUPPORTED_LABELS[code] ?? code.toUpperCase();
        sel.appendChild(opt);
      });
      sel.dataset.populated = "1";
    }
    sel.value = activeLang;
  });
}

export function applyTranslations() {
  document.documentElement.lang = _locale;

  _populateLangSelects(_locale);

  document.querySelectorAll("[data-i18n]").forEach((el) => {
    el.textContent = t(el.dataset.i18n);
  });
  document.querySelectorAll("[data-i18n-html]").forEach((el) => {
    el.innerHTML = t(el.dataset.i18nHtml);
  });
  document.querySelectorAll("[data-i18n-placeholder]").forEach((el) => {
    el.placeholder = t(el.dataset.i18nPlaceholder);
  });
  document.querySelectorAll("[data-i18n-aria]").forEach((el) => {
    el.setAttribute("aria-label", t(el.dataset.i18nAria));
  });
  document.querySelectorAll("[data-i18n-title-attr]").forEach((el) => {
    el.title = t(el.dataset.i18nTitleAttr);
  });

  // Page <title>
  const pageTitleKey = document.documentElement.dataset.i18nPageTitle;
  if (pageTitleKey) document.title = t(pageTitleKey);
}

/**
 * Switch locale: fetches the new locale file, updates strings, re-applies.
 * Returns a Promise so callers can await if needed.
 *
 * @param {string} lang - e.g. 'en' | 'el'
 * @param {{ persist?: boolean, syncFirestore?: boolean }} opts
 */
export async function setLocale(
  lang,
  { persist = true, syncFirestore = false } = {},
) {
  if (!SUPPORTED.includes(lang)) return;

  if (lang === DEFAULT_LOCALE) {
    _strings = _fallback;
  } else {
    try {
      _strings = await _loadStrings(lang);
    } catch (err) {
      console.warn("[i18n] Locale load failed, keeping current:", err);
      return;
    }
  }

  _locale = lang;
  if (persist) localStorage.setItem(STORAGE_KEY, lang);

  applyTranslations();

  // Notify the rest of the app
  document.dispatchEvent(new CustomEvent("localechange", { detail: { lang } }));

  if (syncFirestore) _syncLocaleToFirestore(lang);
}

// ─── Firestore sync (lazy, non-blocking) ──────────────────────────────────────
async function _syncLocaleToFirestore(lang) {
  try {
    const { getAuth } = await import("firebase/auth");
    const { doc, updateDoc } = await import("firebase/firestore");
    const { db } = await import("./firebase.js");
    const user = getAuth().currentUser;
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { locale: lang });
  } catch (_) {
    /* non-critical */
  }
}

// ─── Detection helpers ────────────────────────────────────────────────────────
function _readIpLocaleCache() {
  try {
    const raw = localStorage.getItem(IP_LOCALE_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.locale || !parsed?.expiresAt) return null;
    if (!SUPPORTED.includes(parsed.locale)) return null;
    if (Date.now() > parsed.expiresAt) return null;

    return parsed.locale;
  } catch {
    return null;
  }
}

function _writeIpLocaleCache(locale, ttlMs) {
  try {
    localStorage.setItem(
      IP_LOCALE_CACHE_KEY,
      JSON.stringify({
        locale,
        expiresAt: Date.now() + ttlMs,
      }),
    );
  } catch {
    /* non-critical */
  }
}

function _detectLocaleFromNavigator() {
  const candidates = [navigator.language, ...(navigator.languages || [])]
    .filter(Boolean)
    .map((value) => value.toLowerCase().split("-")[0]);

  return candidates.find((value) => SUPPORTED.includes(value)) ?? null;
}

/**
 * Detects locale by IP geolocation.
 * - Returns a supported locale if the country is mapped.
 * - Returns DEFAULT_LOCALE ("en") for any unmapped country.
 * - Returns DEFAULT_LOCALE ("en") on timeout or error.
 * Results are cached to avoid repeated calls across page loads.
 */
async function _detectLocaleByIp() {
  const cached = _readIpLocaleCache();
  if (cached) {
    console.log(`[i18n] Using cached IP locale: "${cached}"`);
    return cached;
  }

  if (_ipLocalePromise) {
    return _ipLocalePromise;
  }

  _ipLocalePromise = (async () => {
    try {
      const ctrl = new AbortController();
      const id = setTimeout(() => ctrl.abort(), 3000);
      console.log("[i18n] Detecting locale via IP geolocation…");
      const res = await fetch("https://ipapi.co/json/", {
        signal: ctrl.signal,
      });
      clearTimeout(id);

      if (!res.ok) {
        throw new Error(`ipapi ${res.status}`);
      }

      const data = await res.json();
      const country = data?.country_code;
      console.log(`[i18n] IP country detected: ${country}`);

      // Cache the raw country for later use by other modules (e.g. shop save)
      if (country) {
        try {
          localStorage.setItem(
            IP_COUNTRY_CACHE_KEY,
            JSON.stringify({
              code: country,
              name: data?.country_name || "",
              expiresAt: Date.now() + IP_LOCALE_CACHE_TTL_MS,
            }),
          );
        } catch {
          /* non-critical */
        }
      }

      // Map country codes to supported locales.
      // Extend this map when new languages are added.
      const COUNTRY_TO_LOCALE = {
        GR: "el",
        CY: "el",
      };

      const mapped = COUNTRY_TO_LOCALE[country];
      if (mapped && SUPPORTED.includes(mapped)) {
        console.log(`[i18n] Country "${country}" mapped to locale "${mapped}"`);
        _writeIpLocaleCache(mapped, IP_LOCALE_CACHE_TTL_MS);
        return mapped;
      }

      console.log(
        `[i18n] Country "${country}" has no locale mapping → defaulting to "${DEFAULT_LOCALE}"`,
      );
      _writeIpLocaleCache(DEFAULT_LOCALE, IP_LOCALE_CACHE_TTL_MS);
      return DEFAULT_LOCALE;
    } catch (err) {
      console.warn(
        `[i18n] IP detection failed (${err.message}) → defaulting to "${DEFAULT_LOCALE}"`,
      );
      _writeIpLocaleCache(DEFAULT_LOCALE, IP_LOCALE_ERROR_CACHE_TTL_MS);
      return DEFAULT_LOCALE;
    } finally {
      _ipLocalePromise = null;
    }
  })();

  return _ipLocalePromise;
}

// ─── Firestore preference fetch ───────────────────────────────────────────────
async function _fetchFirestoreLocale() {
  try {
    const { getAuth } = await import("firebase/auth");
    const { doc, getDoc } = await import("firebase/firestore");
    const { db } = await import("./firebase.js");
    const user = getAuth().currentUser;
    if (!user) return null;
    const snap = await getDoc(doc(db, "users", user.uid));
    const locale = snap.data()?.locale;
    if (SUPPORTED.includes(locale)) {
      console.log(`[i18n] Firestore locale found: "${locale}"`);
      return locale;
    }
    console.log("[i18n] No valid Firestore locale found");
  } catch (e) {
    console.warn("[i18n] Firestore locale fetch failed:", e.message);
  }
  return null;
}

/**
 * Initialise i18n on page load. Must be awaited before first render.
 * Always loads EN as fallback, then loads the detected/saved locale if different.
 *
 * Detection priority:
 *   1. localStorage (manual user preference)
 *   2. Firestore (authenticated pages only)
 *   3. navigator.language when it matches a supported locale
 *   4. IP geolocation → EN fallback for unmapped countries
 *
 * @param {{ allowFirestore?: boolean }} opts
 * @returns {Promise<string>} resolved locale
 */
export async function initLocale({ allowFirestore = false } = {}) {
  console.log("[i18n] Initialising locale…");

  // Always load EN as the fallback first (ensures missing keys never crash)
  _fallback = await _loadStrings(DEFAULT_LOCALE);

  // ── Determine target locale ──────────────────────────────────────────────
  // Track whether the locale came from an explicit user preference.
  // Only explicit preferences are persisted to localStorage — IP detection is
  // a one-time guess and must NOT be saved so it doesn't lock in a wrong locale.
  let fromExplicitPreference = false;

  // 1. Saved preference (user manually switched language)
  const saved = localStorage.getItem(STORAGE_KEY);
  let target = saved && SUPPORTED.includes(saved) ? saved : null;
  if (target) {
    console.log(`[i18n] Using saved localStorage preference: "${target}"`);
    fromExplicitPreference = true;
  }

  // 2. Firestore preference (authenticated pages — overrides localStorage)
  if (allowFirestore) {
    const remote = await _fetchFirestoreLocale();
    if (remote) {
      console.log(`[i18n] Using Firestore preference: "${remote}"`);
      target = remote;
      fromExplicitPreference = true;
    }
  }

  // 3. Browser locale (guest, no saved preference)
  if (!target) {
    const browserLocale = _detectLocaleFromNavigator();
    if (browserLocale) {
      console.log(`[i18n] Using browser locale: "${browserLocale}"`);
      target = browserLocale;
    }
  }

  // 4. IP geolocation (guest, no saved preference) — never persisted to localStorage
  if (!target) {
    target = await _detectLocaleByIp();
  }

  // ── Load active locale ───────────────────────────────────────────────────
  if (target === DEFAULT_LOCALE) {
    _strings = _fallback;
    _locale = DEFAULT_LOCALE;
  } else {
    try {
      _strings = await _loadStrings(target);
      _locale = target;
    } catch {
      // Gracefully fall back to EN if locale file is missing
      console.warn(
        `[i18n] Locale file for "${target}" not found → falling back to "${DEFAULT_LOCALE}"`,
      );
      _strings = _fallback;
      _locale = DEFAULT_LOCALE;
      target = DEFAULT_LOCALE;
    }
  }

  // Only persist to localStorage if this was an explicit user preference,
  // not an automatic IP detection result.
  if (fromExplicitPreference) {
    localStorage.setItem(STORAGE_KEY, _locale);
  }

  applyTranslations();

  console.log(
    `[i18n] Active locale set to: "${_locale}" (persisted: ${fromExplicitPreference})`,
  );
  return _locale;
}
